import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faArrowTrendUp,
  faBuilding,
  faFilter,
  faLocationDot,
  faMagnifyingGlass,
  faUserDoctor,
} from '@fortawesome/pro-solid-svg-icons';

// Largest CSV the Scout chat accepts. Mirrors the backend's MAX_FILE_BYTES
// (medscout-api scout_chat_consumer), which rejects anything bigger.
export const MAX_ATTACHMENT_BYTES = 500 * 1024;

/** Route of the Discovery map, where "View on Map" applies an AI search. */
export const DISCOVERY_PATHNAME = '/search';

/**
 * How far (in degrees, ~11 m) Discovery's radius center may drift from an AI
 * search's center and still count as showing that search's area.
 */
export const APPLIED_CENTER_TOLERANCE_DEG = 1e-4;

/** Drawer id for the Scout AI chat drawer (useDrawer). */
export const SCOUT_DRAWER_ID = 'scout-ai';

/** Route of the Scout dashboard page. */
export const SCOUT_PAGE_PATH = '/scout';

/** Where "Back to drawer" goes when /scout was the first page visited. */
export const SCOUT_FALLBACK_ROUTE = '/';

/** Chat panel widths the dashboard's resize handle snaps to (px → existing Tailwind width class). */
export const CHAT_PANEL_WIDTHS = [
  { px: 448, className: 'w-112' },
  { px: 512, className: 'w-128' },
  { px: 576, className: 'w-144' },
  { px: 640, className: 'w-160' },
  { px: 704, className: 'w-176' },
  { px: 768, className: 'w-192' },
] as const;
export const DEFAULT_CHAT_WIDTH_INDEX = 3;

/**
 * 16:9 pages (PowerPoint's widescreen size) so each slide fills its page,
 * with chart and tile colours kept.
 */
export const PRINT_PAGE_STYLE =
  '@page { size: 13.333in 7.5in; margin: 0 } @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }';

/**
 * How long a PDF export may go without react-to-print reporting back before
 * it's treated as over. Some of its failures only log and call neither
 * `onAfterPrint` nor `onPrintError`; the print copy is already cloned by then.
 */
export const PRINT_RESET_MS = 10_000;

export const ALLOWED_ROUTE_PREFIXES = [
  '/profiles/',
  '/strategy-center',
  '/search',
  '/territory-manager',
  '/lists/',
  '/activity',
  '/analytics',
  '/exports',
  '/settings',
];

export const ROUTE_LABELS: Record<string, string> = {
  '/': 'Strategies',
  '/strategy-center': 'Strategies',
  '/search': 'Discovery',
  '/activity': 'Activity',
  '/lists': 'Lists',
  '/analytics': 'Analytics',
  '/territory-manager': 'Territories',
  '/exports': 'Exports',
  '/settings': 'Settings',
  '/global-reports': 'Reports',
  '/notifications': 'Notifications',
};

/**
 * Scout's brand gradient, cyan into blue. Matches the mobile avatar
 * (MOB-82). Off-palette by design — Figma owns these two stops.
 */
export const SCOUT_GRADIENT = {
  from: '#00A8C8',
  to: '#0084D1',
} as const;

/** Recent conversations shown on the empty state before the full history view. */
export const EMPTY_STATE_RECENT_LIMIT = 3;

/**
 * Suggestion-card icons, keyed by templateId from buildDynamicSuggestions.
 * Decorative only — the card text carries the meaning.
 */
export const SUGGESTION_ICONS: Record<string, IconDefinition> = {
  find_providers: faMagnifyingGlass,
  route_top_sites: faLocationDot,
  filter_set_search: faFilter,
  filter_set_soc_sites: faBuilding,
  top_providers: faUserDoctor,
  volume_trend: faArrowTrendUp,
  static_example: faMagnifyingGlass,
};

export const SUGGESTION_ICON_FALLBACK = faMagnifyingGlass;

/** Time-of-day greeting prefix. */
export function getGreetingPrefix(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}
